import { defineStore } from "pinia";
import { ref } from "vue";
import {
  collection, query, where, limit, onSnapshot, type Unsubscribe,
} from "firebase/firestore";
import { db } from "../firebase";
import { useAuthStore } from "./auth";
import { useWorkspaceStore } from "./workspace";

/**
 * Journal d'activités (Lot 7) — flux temps réel, lu depuis le compte
 * propriétaire (`dataOwnerUid`). Écritures = Cloud Functions (logActivity).
 *
 * Portée (scope) :
 *   - { section }            → toute la section (liste)
 *   - { section, targetId }  → un enregistrement précis (soumission / dossier)
 *   - { }                    → flux global récent (sections sans journal dédié)
 *
 * On évite les index composites : requête à égalité simple (ou aucune) + tri
 * côté client par `at` décroissant.
 */
export interface ActivityEntry {
  id: string;
  section: string;
  action: string;
  label: string;
  details?: string[];
  targetId?: string | null;
  actorUid: string;
  actorEmail: string;
  at?: any;
}

function toMs(at: any): number {
  if (!at) return 0;
  if (at.toMillis) return at.toMillis();
  if (at.seconds) return at.seconds * 1000;
  return 0;
}

export const useActivityStore = defineStore("activity", () => {
  const entries = ref<ActivityEntry[]>([]);
  const loading = ref(false);
  let unsub: Unsubscribe | null = null;
  let currentKey: string | null = null;

  async function subscribeScope(section?: string, targetId?: string): Promise<void> {
    const auth = useAuthStore();
    if (!auth.user) return;
    const key = `${section ?? ""}|${targetId ?? ""}`;
    if (currentKey === key && unsub) return;

    const workspace = useWorkspaceStore();
    await workspace.ensureLoaded();
    const ownerUid = workspace.ownerUid();
    if (!ownerUid) return;

    if (unsub) { unsub(); unsub = null; }
    currentKey = key;
    loading.value = true;

    const col = collection(db, "users", ownerUid, "activities");
    let q;
    if (targetId) q = query(col, where("targetId", "==", targetId), limit(200));
    else if (section) q = query(col, where("section", "==", section), limit(200));
    else q = query(col, limit(200));

    unsub = onSnapshot(
      q,
      (snap) => {
        const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() } as ActivityEntry));
        rows.sort((a, b) => toMs(b.at) - toMs(a.at));
        entries.value = rows.slice(0, 80);
        loading.value = false;
      },
      () => { loading.value = false; },
    );
  }

  function unsubscribe(): void {
    if (unsub) { unsub(); unsub = null; }
    currentKey = null;
    entries.value = [];
  }

  return { entries, loading, subscribeScope, unsubscribe };
});
